'use client';

import { ReactNode, useMemo } from 'react';

import {
    Spacer,
    SpacerSize,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
    TypographyBold,
} from '@bytel/trilogy-react';

import { SmartChangeIcon } from '@components/smart-change';

export enum CategoryTemplateType {
    SMART_CHANGE = 'smart-change',
    SMALL_PRICE = 'small-price',
    DEFAULT = 'default',
}

type CategoryTemplateProps = {
    hasOnlySmartChangePlans: boolean;
    isSmallPriceCategory: boolean;
    categorySubtitle?: string;
    children: ReactNode;
};

export function CategoryTemplate({
    hasOnlySmartChangePlans,
    isSmallPriceCategory,
    categorySubtitle,
    children,
}: Readonly<CategoryTemplateProps>) {
    const templateType = useMemo((): CategoryTemplateType => {
        if (hasOnlySmartChangePlans) {
            return CategoryTemplateType.SMART_CHANGE;
        }

        if (isSmallPriceCategory) {
            return CategoryTemplateType.SMALL_PRICE;
        }

        return CategoryTemplateType.DEFAULT;
    }, [hasOnlySmartChangePlans, isSmallPriceCategory]);

    const categoryTitle = useMemo((): ReactNode => {
        switch (templateType) {
            case CategoryTemplateType.SMART_CHANGE:
                return (
                    <div className='has-text-centered'>
                        <Title
                            level={TitleLevels.FOUR}
                            markup={TitleMarkup.H3}
                            typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                        >
                            Les forfaits disponibles avec
                        </Title>
                        <SmartChangeIcon height={32} />
                        <Spacer size={SpacerSize.FIVE} />
                    </div>
                );
            case CategoryTemplateType.DEFAULT:
            default:
                return (
                    <Title
                        level={TitleLevels.THREE}
                        markup={TitleMarkup.H3}
                        typo={[TypographyAlign.TEXT_CENTERED, TypographyBold.TEXT_WEIGHT_SEMIBOLD]}
                    >
                        {categorySubtitle}
                    </Title>
                );
        }
    }, [templateType, categorySubtitle]);

    return (
        <>
            {categoryTitle}
            {children}
        </>
    );
}
